<template>
    <breadcrumbs />
    <ModeSwitcher ref="modeSwitcher" :main-mode-text="$t('create')" :create-mode-text="$t('create_kpi')">
        <template v-slot:btn-block>
            <div class="full-width d-flex align-center">
                <v-spacer />
                <FilterStatusToggle v-model="filters.active" @update:model-value="loadData" />
            </div>
        </template>
        <template v-slot:main-mode>
            <v-data-table-server
                :headers="useHeaders"
                :items="items"
                :items-per-page-options="useTableOptions(totalCount)"
                :options="tableOptions"
                :loading="isLoading"
                :items-length="totalCount"
                disable-sort
                density="compact"
                color="primary"
                class="elevation-1 table-server"
                data-cy="kpi-table"
                @update:options="onUpdateTableOptions"
            >
                <template v-slot:item.name="{item}">
                    <nuxt-link no-prefetch :to="{name: 'kpi', params: {id: item.id}}" :data-cy="`kpi-${item.id}`">
                        {{ item.name }}
                    </nuxt-link>
                </template>
                <template v-slot:item.period="{item}">
                    {{ $filters.dateShortFormat(item.startDate) }} - {{ $filters.dateShortFormat(item.endDate) }}
                </template>
                <template v-slot:item.kpi="{item}">
                    <template v-if="item.paymentKpis">
                        <span v-for="(kpi, index) in item.paymentKpis" :key="kpi.id">
                            <span class="font-weight-bold"> {{ $t(`paymentKpis.${kpi?.priceType}`) }}: </span>
                            <span v-if="kpi?.price && kpi?.currencyCode">
                                {{ $filters.priceFormat(kpi?.price, kpi?.currencyCode) }}
                                {{ item.paymentKpis?.length > 1 && index === 0 ? `, ` : '' }}
                            </span>
                        </span>
                    </template>
                </template>
                <template v-slot:item.active="{item}">
                    <v-switch
                        v-model="item.active"
                        color="primary"
                        :label="item.active ? $t('active') : $t('inactive')"
                        hide-details
                        :data-cy="`status-${item.id}`"
                        @update:model-value="changeStatus(item)"
                    />
                </template>
                <template v-slot:item.actions="{item}">
                    <div class="d-flex align-center justify-end nowrap">
                        <nuxt-link
                            no-prefetch
                            :to="{
                                name: 'kpi',
                                params: {id: item.id},
                                query: {productTab: 'tab-bi'},
                            }"
                            class="mr-2"
                        >
                            <v-icon size="small">mdi-chart-areaspline-variant</v-icon>
                        </nuxt-link>
                        <nuxt-link
                            no-prefetch
                            :to="{
                                name: 'kpi',
                                params: {id: item.id},
                                query: {productTab: 'tab-information'},
                            }"
                        >
                            <v-icon size="small" class="mr-2">mdi-information-outline</v-icon>
                        </nuxt-link>
                    </div>
                </template>
            </v-data-table-server>
        </template>
        <template v-slot:additional-mode>
            <KpiForm :kpi-id="0" @cancel="modeSwitcher.goToTable()" />
        </template>
    </ModeSwitcher>
</template>

<script setup lang="ts">
    import Breadcrumbs from '~src/components/Breadcrumbs.vue'
    import KpiForm from '~/components/kpi/KpiForm.vue'

    const isLoading = ref<boolean>(false)
    const filters = ref({active: true})
    const items = ref<any[]>([])
    const totalCount = ref<number>(0)
    const tableOptions = ref({
        itemsPerPage: 10,
        page: 1,
    })
    const modeSwitcher = ref(null)

    const app = useNuxtApp()
    const {t} = useI18n()

    async function loadData(): Promise<void> {
        const {itemsPerPage, page} = tableOptions.value

        try {
            isLoading.value = true
            const rq = {...filters.value}
            clean(rq)
            if (itemsPerPage !== -1) Object.assign(rq, {limit: itemsPerPage, offset: itemsPerPage * (page - 1)})

            const {apiKpis, count} = await app.$api.kpi.get(rq)
            items.value = apiKpis
            totalCount.value = count
        } catch (e) {
            items.value = []
        } finally {
            isLoading.value = false
        }
    }

    const useHeaders = computed(() => {
        return [
            {title: t('title'), value: 'name', minWidth: '120px'},
            {title: t('period'), value: 'period'},
            {title: t('KPI', 2), value: 'kpi', minWidth: '180px'},
            {title: t('status'), value: 'active', width: '140px', minWidth: '140px'},
            {title: '', value: 'actions', width: '140px', minWidth: '140px'},
        ]
    })

    const onUpdateTableOptions = (options): void => {
        tableOptions.value = {itemsPerPage: options.itemsPerPage, page: options.page}
        loadData()
    }

    const changeStatus = async (item): Promise<void> => {
        const {id, active} = item
        try {
            isLoading.value = true
            await app.$api.kpi.patch(id, {active})
            await loadData()
            app.$toast.success(t('success_message.changed_successfully'))
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }
</script>
